import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import TextReveal from './TextReveal';
import { client, urlFor } from '../sanityClient';

const defaultSkills = [
  'Space Planning',
  'Residential Design',
  'Design Consulting',
  'Brutalist Styling',
  'Lighting Architecture'
];

const defaultMarquee = [
  'quiet luxury',
  'material integrity',
  'bespoke craftsmanship'
];

export default function Hero() {
  const [skillIndex, setSkillIndex] = useState(0);
  const [heroData, setHeroData] = useState(null);

  useEffect(() => {
    client.fetch('*[_type == "hero"][0]')
      .then((data) => {
        setHeroData(data);
      })
      .catch(console.error);
  }, []);

  const skills = heroData?.skills && heroData.skills.length > 0 ? heroData.skills : defaultSkills;
  const marqueeWords = heroData?.marqueeWords && heroData.marqueeWords.length > 0 ? heroData.marqueeWords : defaultMarquee;
  const headline = heroData?.headline || "Designing Spaces. Defining Stories.";
  const bio = heroData?.bio || "At POMIE, we create luxury interiors where flow, form, and feeling exist in perfect balance. Inspired by Islamabad's refined modernism and Rawalpindi's rich architectural heritage, we design spaces that celebrate authenticity through honest materials, thoughtful craftsmanship, and timeless design. Every environment is carefully curated to evoke emotion, elevate everyday living, and reflect the unique identity of those who inhabit it.";
  const imageSrc = heroData?.image ? urlFor(heroData.image).width(1200).quality(80).auto('format').url() : '/hero_interior.webp';
  const srcSet = heroData?.image
    ? `${urlFor(heroData.image).width(400).quality(80).auto('format').url()} 400w, ${urlFor(heroData.image).width(800).quality(80).auto('format').url()} 800w, ${urlFor(heroData.image).width(1200).quality(80).auto('format').url()} 1200w`
    : undefined;
  const basedInLabel = heroData?.basedInLabel || "Based In";
  const basedInValue = heroData?.basedInValue || "Islamabad — Rawalpindi";
  const servicesLabel = heroData?.servicesLabel || "Services";
  const servicesValue = heroData?.servicesValue || "Space Planning & Interior Architecture";

  useEffect(() => {
    const timer = setInterval(() => {
      setSkillIndex((prev) => (prev + 1) % skills.length);
    }, 2800);
    return () => clearInterval(timer);
  }, [skills.length]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const fadeUpVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 1.2,
        ease: [0.16, 1, 0.3, 1],
      },
    },
  };

  const imageVariants = {
    hidden: { opacity: 0, scale: 0.95, y: 20 },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: {
        duration: 1.4,
        ease: [0.16, 1, 0.3, 1],
      },
    },
  };

  const lineVariants = {
    hidden: { scaleX: 0 },
    visible: {
      scaleX: 1,
      transition: {
        duration: 1.5,
        ease: [0.16, 1, 0.3, 1],
        delay: 0.8,
      },
    },
  };

  return (
    <section id="hero" aria-label="Hero Showcase and Creative Intro" className="relative min-h-screen w-full flex flex-col justify-between px-4 md:px-8 lg:px-12 pt-32 pb-12 bg-transparent overflow-x-hidden">
      {/* Main Grid Content - Centered Vertically */}
      <div className="flex-grow flex items-center justify-center max-w-7xl mx-auto w-full z-10 py-4">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center w-full"
        >
          {/* Left Side: Headline & Bio */}
          <div className="lg:col-span-7 flex flex-col justify-center text-left min-w-0">
            <motion.div
              variants={fadeUpVariants}
              className="text-xs font-sans tracking-widest uppercase text-[#9c1b42] opacity-85 mb-4 md:mb-6 flex flex-row items-center gap-2 min-h-[1.75rem] py-0.5 overflow-hidden"
            >
              <span className="opacity-90">// Specializing in</span>
              <span className="font-bold tracking-widest relative">
                <AnimatePresence mode="wait">
                  <motion.span
                    key={skillIndex}
                    initial={{ y: 16, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: -16, opacity: 0 }}
                    transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                    className="inline-block"
                  >
                    {skills[skillIndex]}
                  </motion.span>
                </AnimatePresence>
              </span>
            </motion.div>

            {/* Animated Text Reveal Headline */}
            <TextReveal
              as="h1"
              key={headline}
              text={headline}
              animateOnLoad={true}
              className="text-[#9c1b42] font-manrope font-extrabold uppercase leading-[1.05] tracking-tighter text-3xl sm:text-4xl md:text-4xl lg:text-4xl xl:text-[3.5vw] mb-4 md:mb-6 w-full"
              delay={0.3}
            />
            <motion.p
              variants={fadeUpVariants}
              className="text-[#9c1b42]/75 font-sans text-base md:text-lg font-light tracking-wide leading-relaxed max-w-xl"
            >
              {bio}
            </motion.p>
          </div>

          {/* Right Side: Feature Image */}
          <div className="lg:col-span-5 w-full flex justify-center lg:justify-end">
            <motion.div
              variants={imageVariants}
              className="relative w-full max-w-[380px] aspect-[4/5] border border-[#9c1b42] p-2 bg-[#f4ceb3] overflow-hidden group shadow-lg"
            >
              {/* Fine background graph paper texture inside the frame */}
              <div className="absolute inset-0 pointer-events-none opacity-20"
                style={{
                  backgroundImage: 'radial-gradient(#9c1b42 0.5px, transparent 0.5px)',
                  backgroundSize: '16px 16px'
                }}
              />

              <div className="w-full h-full overflow-hidden relative">
                <img
                  src={imageSrc}
                  srcSet={srcSet}
                  sizes={srcSet ? "(max-width: 768px) 100vw, 50vw" : undefined}
                  alt="Minimalist luxury interior design project by Pomie Studio"
                  loading="eager"
                  fetchpriority="high"
                  className="w-full h-full object-cover object-center filter contrast-[1.02] transition-transform duration-700 ease-out group-hover:scale-[1.03]"
                />
                <div className="absolute inset-0 bg-[#9c1b42]/5 mix-blend-multiply pointer-events-none" />
              </div>

              {/* Minimal coordinates/metadata in the corner of image card */}
              <span className="absolute bottom-4 right-4 text-[9px] font-sans tracking-widest text-[#f4ceb3] bg-[#9c1b42] px-2 py-0.5 uppercase">
                Studio View // {new Date().getFullYear()}
              </span>
            </motion.div>
          </div>
        </motion.div>
      </div>

      {/* Infinite Horizontal Marquee Ticker */}
      <div className="relative left-1/2 -translate-x-1/2 w-screen overflow-hidden border-y border-[#9c1b42]/15 py-4 my-8 bg-[#9c1b42]/5 z-10">
        <div className="flex w-max animate-marquee">
          <div className="flex gap-8 md:gap-16 pr-8 md:pr-16 shrink-0 whitespace-nowrap text-[10px] md:text-xs font-sans tracking-[0.2em] uppercase font-bold text-[#9c1b42]">
            {marqueeWords.map((word, idx) => (
              <span key={`marquee-1-${idx}`}>★ {word}</span>
            ))}
          </div>
          <div className="flex gap-8 md:gap-16 pr-8 md:pr-16 shrink-0 whitespace-nowrap text-[10px] md:text-xs font-sans tracking-[0.2em] uppercase font-bold text-[#9c1b42]">
            {marqueeWords.map((word, idx) => (
              <span key={`marquee-2-${idx}`}>★ {word}</span>
            ))}
          </div>
        </div>
      </div>

      {/* Editorial Details Footer of Hero - Placed at the very bottom */}
      <div className="max-w-7xl mx-auto w-full z-10 mt-auto">
        {/* Animated Horizontal Line separator */}
        <motion.div
          className="w-full h-[1px] bg-[#9c1b42]/30 origin-left mb-6"
          initial="hidden"
          animate="visible"
          variants={lineVariants}
        />

        <motion.div
          className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-6 text-[#9c1b42] font-sans text-[10px] md:text-xs tracking-widest uppercase w-full"
          initial="hidden"
          animate="visible"
          variants={fadeUpVariants}
        >
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-16 text-left w-full sm:w-auto">
            <div>
              <span className="opacity-90 block mb-1">{basedInLabel}</span>
              <span className="font-semibold text-[#9c1b42]">{basedInValue}</span>
            </div>
            <div>
              <span className="opacity-90 block mb-1">{servicesLabel}</span>
              <span className="font-semibold text-[#9c1b42]">{servicesValue}</span>
            </div>
          </div>

          <div className="flex items-center justify-between sm:justify-end gap-4 w-full sm:w-auto border-t sm:border-t-0 border-[#9c1b42]/10 pt-4 sm:pt-0">
            <span className="opacity-90 text-[#9c1b42]">Scroll to explore</span>
            <motion.div
              animate={{ y: [0, 6, 0] }}
              transition={{ repeat: Infinity, duration: 1.5, ease: "easeInOut" }}
              className="w-4 h-7 border border-[#9c1b42] rounded-full flex justify-center pt-1"
            >
              <div className="w-1 h-1.5 bg-[#9c1b42] rounded-full" />
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
